import { useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import type { BreadcrumbLink } from '#/components/Breadcrumbs/Breadcrumbs';
import { Breadcrumbs } from '#/components/Breadcrumbs/Breadcrumbs';
import { Byline } from '#/components/Byline/Byline';
import { H1 } from '#/components/Text/H1';
import type { CmsApiPage } from '#/modules/cms/api/types/CmsApiPage';
import { CmsCategories } from '#/modules/cms/components/Categories/CmsCategories';
import { getIsContentFullWidthLayout } from '#/modules/cms/helpers/getIsContentFullWidthLayout';
import { CmsPostPageBodyHeaderBreadcrumbsHolder } from '#/modules/cms/pages/Post/Body/CmsPostPageBodyHeader.styled';
import { useIsClient } from '#/hooks/useIsClient';
import type { CmsPostPageLocationState } from '#/modules/cms/pages/Post/CmsPostPageLocationState';
import { useLocation } from 'react-router';

type Props = {
  content: CmsApiPage;
};

export function CmsPostPageBodyHeader({ content }: Props) {
  const fullWidthLayout = getIsContentFullWidthLayout({ content });

  const isClient = useIsClient();
  const location = useLocation<CmsPostPageLocationState>();
  const subsite = isClient
    ? (location.state?.subsite ?? content.subsite)
    : content.subsite;

  const links: BreadcrumbLink[] = useMemo(
    () => [
      {
        title: subsite.publicName,
        to: `/${subsite.homePage?.url}`,
        qaId: 'breadcrumbs-home-link',
      },
      {
        title: content.title,
        to: `/${content.url}`,
        state: {
          subsite,
        },
      },
    ],
    [subsite, content],
  );

  return (
    <>
      {content.type === 'POST' && subsite.homePage ? (
        <CmsPostPageBodyHeaderBreadcrumbsHolder>
          <Breadcrumbs links={links} />
        </CmsPostPageBodyHeaderBreadcrumbsHolder>
      ) : undefined}

      {content.type === 'POST' &&
        content.categories &&
        content.categories.length > 0 && (
          <Box mb={24}>
            <CmsCategories
              categories={content.categories}
              homePageUrl={content.subsite.homePage?.url}
            />
          </Box>
        )}

      <H1 m="0" textAlign={fullWidthLayout ? 'center' : 'left'}>
        {content.title}
      </H1>

      {(content.author ?? (content.postedDate && content.showDate)) && (
        <Box mt={4}>
          <Byline
            slug={content.author ? content.author.slug : undefined}
            name={content.author ? content.author.name : undefined}
            image={content.author?.image ?? undefined}
            homePageUrl={content.subsite.homePage?.url}
            postedDate={content.postedDate}
            showDate={content.showDate}
          />
        </Box>
      )}
    </>
  );
}
